import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Lock, Eye, EyeOff, ArrowLeft, Tag, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPasswordPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(i18n.language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError(i18n.language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || (i18n.language === 'ar' ? 'الرابط غير صالح أو منتهي الصلاحية' : 'Invalid or expired reset link'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {i18n.language === 'ar' ? 'رابط غير صالح' : 'Invalid Link'}
          </h2>
          <p className="text-gray-600 mb-6">
            {i18n.language === 'ar' ? 'رابط إعادة التعيين غير صالح. حاول طلب رابط جديد.' : 'This reset link is invalid. Please request a new one.'}
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="w-full py-3 bg-[#E31C25] text-white rounded-lg font-semibold hover:bg-[#C41820] transition-colors"
            data-testid="go-to-login-btn"
          >
            {i18n.language === 'ar' ? 'الذهاب لتسجيل الدخول' : 'Go to Login'}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {i18n.language === 'ar' ? 'تم تغيير كلمة المرور!' : 'Password Changed!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {i18n.language === 'ar' ? 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.' : 'You can now log in with your new password.'}
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="w-full py-3 bg-[#E31C25] text-white rounded-lg font-semibold hover:bg-[#C41820] transition-colors"
            data-testid="login-after-reset-btn"
          >
            {i18n.language === 'ar' ? 'تسجيل الدخول' : 'Log In'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-24 md:pb-8">
      <div className="max-w-md mx-auto px-4 py-6">
        <button 
          onClick={() => navigate('/auth')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{i18n.language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}</span>
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#E31C25] rounded-xl flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 font-heading mb-2">
              {i18n.language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter your new password'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-[#E31C25] text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {i18n.language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-11 pr-12 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25] transition-colors"
                  placeholder="••••••••"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {i18n.language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25] transition-colors"
                  placeholder="••••••••"
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#E31C25] text-white rounded-lg font-semibold hover:bg-[#C41820] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{i18n.language === 'ar' ? 'جاري التحديث...' : 'Updating...'}</span>
                </div>
              ) : (
                i18n.language === 'ar' ? 'تعيين كلمة المرور الجديدة' : 'Set New Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
